import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/hooks/useDocumentMeta";

/**
 * Privacy Policy — written for parents, educators, and district reviewers.
 * Clear, plain-language, K-12 first. COPPA + FERPA-aware. Effective date
 * is rendered dynamically from build time; revisit yearly.
 */
const LAST_UPDATED = "February 5, 2026";

export default function PrivacyPolicyPage() {
  useDocumentMeta({
    title: "Privacy Policy | Lemonade Stand Business Plan",
    description:
      "How The Lemonade Stand Business Plan collects, uses, and protects K-12 student data. Plain-language, COPPA + FERPA aware policy from The WE Mentality.",
    keywords:
      "privacy policy, student data privacy, K-12 educational technology, FERPA, COPPA, entrepreneurship education",
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white flex flex-col">
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14 flex-1 w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm mb-6"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <header className="mb-8">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <Shield className="w-3.5 h-3.5" /> Student Data Privacy
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        <section className="prose prose-sm sm:prose-base max-w-none text-gray-800 leading-relaxed space-y-6">
          <p>
            The Lemonade Stand Business Plan ("the Platform") is operated by
            <strong> The WE Mentality</strong> (Las Vegas, Nevada). We build
            entrepreneurship and career-readiness experiences for K-12 students,
            their teachers, and their families. This Privacy Policy explains —
            in plain language — what we collect, why, and the choices you have.
          </p>

          <div className="bg-green-50 border-l-4 border-green-400 rounded-r-2xl p-4 my-6">
            <p className="text-sm font-semibold text-green-900 mb-1">
              In one sentence:
            </p>
            <p className="text-sm text-green-900">
              We collect only what's needed to run lessons and show progress, we
              never sell student data, and parents/schools can request deletion
              at any time.
            </p>
          </div>

          <h2 className="text-xl font-bold text-gray-900 pt-2">1. Who this policy is for</h2>
          <p>
            This policy applies to anyone who uses the Platform — students,
            educators, parents/guardians, school administrators, and visitors
            browsing our public pages.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">2. What we collect</h2>
          <p>We try to collect as little as possible. Here's the full list:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Account information</strong> — name, email, and role
              (student, educator, parent, admin). Educators may also provide a
              school name.
            </li>
            <li>
              <strong>Learning activity</strong> — lesson responses, quiz
              answers, reflections, and your Mini Business Plan as you build
              it. This is the actual product.
            </li>
            <li>
              <strong>Optional pitch recordings</strong> — only if you choose
              to record one. Stored solely to score and replay.
            </li>
            <li>
              <strong>Aggregate usage</strong> — anonymized counts (e.g., how
              many lessons were completed this week), used to improve the
              Platform.
            </li>
            <li>
              <strong>Light technical data</strong> — IP address (for security
              and rate-limiting), browser type, and referring page. This
              auto-deletes after 30 days unless tied to a security incident.
            </li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 pt-2">3. What we do <em>not</em> collect</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Social-security numbers, government IDs, or financial information.</li>
            <li>Precise location (GPS).</li>
            <li>Health, biometric, or sensitive demographic data.</li>
            <li>Targeted-advertising profiles. We don't run ads on the Platform.</li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 pt-2">4. Students under 13 (COPPA)</h2>
          <p>
            Our Elementary pathway ("Lemonade Leaders Jr." — grades 2-5) is
            currently <strong>preview-only</strong>: nothing is sent to our
            servers, and all progress is stored in the child's own browser. We
            do not collect personal information from children under 13 through
            this experience.
          </p>
          <p>
            For any future student accounts under 13, we will only create them
            with <strong>verifiable consent</strong> from a parent, legal
            guardian, or school acting under FERPA "school official" exception
            with parental notice. Schools may sign our standard Student Data
            Privacy Agreement on behalf of students under 13.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">5. FERPA & schools</h2>
          <p>
            When a school or district uses the Platform, we act as a "school
            official" under FERPA (34 CFR § 99.31(a)(1)). That means:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Student education records remain under the school's direction.</li>
            <li>We use student data only to deliver the service the school requested.</li>
            <li>We do not disclose student data to anyone the school hasn't authorized.</li>
            <li>Schools can request export or deletion at any time.</li>
          </ul>
          <p>
            We will sign your district's Student Data Privacy Agreement on
            request. Email{" "}
            <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
              privacy@thewementality.com
            </a>{" "}
            to start.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">6. AI features &amp; responsible use</h2>
          <p>
            "LemonBot" is our AI coach. It helps students think — it does not
            write their work for them. When a student chats with LemonBot:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              The conversation is sent to OpenAI for processing under their
              Enterprise/API agreement (no training on customer data).
            </li>
            <li>We do not store the conversation for AI-model training.</li>
            <li>Educators can disable LemonBot for any class at any time.</li>
            <li>
              We design prompts to be Socratic — LemonBot asks questions, it
              doesn't hand over answers.
            </li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 pt-2">7. How we share data</h2>
          <p>
            We share data <strong>only</strong> with the service providers we
            need to run the Platform. See our{" "}
            <Link to="/subprocessors" className="text-yellow-700 underline">
              Subprocessors list
            </Link>{" "}
            for the full, current list. We never sell student data. We never
            share student data for advertising.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">8. How long we keep data</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>Active accounts: while you're using the Platform.</li>
            <li>
              Inactive accounts (no login for 18 months): we email you, then
              archive.
            </li>
            <li>
              On request: we delete your account and learning data within
              30 days.
            </li>
            <li>Backups: rotated and overwritten within 90 days.</li>
            <li>Aggregate usage metrics: retained indefinitely (no PII).</li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 pt-2">9. Your rights</h2>
          <p>You (or your parent/school on your behalf) can always:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>See what's in your account (download your Mini Business Plan PDF + Workbook).</li>
            <li>
              Correct anything that's wrong — use the in-app profile editor.
            </li>
            <li>
              Delete your account and all associated data — visit{" "}
              <Link to="/delete-account" className="text-yellow-700 underline">
                /delete-account
              </Link>{" "}
              or email us.
            </li>
            <li>
              Ask us a question — email{" "}
              <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
                privacy@thewementality.com
              </a>{" "}
              and we'll respond within 5 business days.
            </li>
          </ul>

          <h2 className="text-xl font-bold text-gray-900 pt-2">10. Security</h2>
          <p>
            We use industry-standard practices: HTTPS everywhere, bcrypt
            password hashing, JWT tokens, rate-limiting against brute-force
            attempts, restricted access controls inside our team, and Sentry
            error tracking with personal-information redaction. We host on
            U.S.-based Emergent Cloud (Kubernetes) and MongoDB Atlas.
          </p>
          <p>
            If we ever discover a breach affecting student data, we will
            notify affected schools and parents within 72 hours.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">11. Cookies &amp; analytics</h2>
          <p>
            We use a small set of cookies/local storage for: login sessions,
            language preference, and accessibility settings. We use anonymized,
            privacy-respecting analytics (PostHog &amp; Google Analytics 4) to
            count page views — these are configured to <em>not</em> capture
            personal information.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">12. Changes to this policy</h2>
          <p>
            If we make material changes, we'll email account holders at least
            30 days before the change takes effect. The "last updated" date
            above always reflects the current version.
          </p>

          <h2 className="text-xl font-bold text-gray-900 pt-2">13. Contact us</h2>
          <p>
            <strong>The WE Mentality</strong>
            <br />
            Las Vegas, Nevada, USA
            <br />
            Privacy contact:{" "}
            <a href="mailto:privacy@thewementality.com" className="text-yellow-700 underline">
              privacy@thewementality.com
            </a>
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mt-8 text-sm">
            <p className="font-semibold text-yellow-900 mb-1">
              Districts &amp; schools
            </p>
            <p className="text-yellow-900">
              We're happy to sign your district's Student Data Privacy Agreement
              and complete vendor-review questionnaires. Email us and we'll
              respond within 2 business days.
            </p>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
